import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { seedApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const { login, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated && user) {
            // Redirection par défaut vers Briefing du jour
            navigate('/briefing');
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => {
        // Auto-seed database on first load
        const initDatabase = async () => {
            try {
                setSeeding(true);
                await seedApi.seed();
            } catch (error) {
                console.log('Database already seeded or error:', error);
            } finally {
                setSeeding(false);
            }
        };
        initDatabase();
    }, []);

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }

        setLoading(true);
        const maxAttempts = 5;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const userData = await login(email, password);
                toast.success(`Bienvenue, ${userData.name}`);
                // Redirection vers Briefing du jour après connexion
                navigate('/briefing');
                setLoading(false);
                return;
            } catch (error) {
                console.error('Login error:', error);
                if (error.response) {
                    // Le serveur a répondu : identifiants incorrects ou autre erreur applicative
                    toast.error(error.response.data?.detail || 'Erreur de connexion');
                    setLoading(false);
                    return;
                }
                // Pas de réponse : le serveur est probablement en train de démarrer
                if (attempt < maxAttempts) {
                    toast.info(`Le serveur démarre, nouvelle tentative ${attempt}/${maxAttempts - 1}...`);
                    await sleep(4000);
                } else {
                    toast.error('Serveur injoignable. Patientez 30 secondes puis réessayez.');
                }
            }
        }
        setLoading(false);
    };

    return (
        <div className="login-split min-h-screen">
            {/* Left side - Image */}
            <div 
                className="login-image hidden md:block relative bg-cover bg-center"
                style={{ 
                    backgroundImage: `url('/images/terrasse-allforyou.jpg')` 
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-primary/40 to-primary/80" />
                <div className="absolute inset-0 flex items-end p-12">
                    <div className="text-white">
                        <h2 className="text-4xl font-heading font-bold mb-4">
                            Pilotage groupe allforyou
                        </h2>
                        <p className="text-lg opacity-90">
                            Pilotez vos restaurants avec précision
                        </p>
                    </div>
                </div>
            </div>

            {/* Right side - Login form */}
            <div className="flex items-center justify-center p-8 bg-background">
                <Card className="w-full max-w-md border-border shadow-lg" data-testid="login-card">
                    <CardHeader className="space-y-1 text-center">
                        <div className="mx-auto mb-4">
                            <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                                <span className="text-primary-foreground text-2xl font-heading font-bold">R</span>
                            </div>
                        </div>
                        <CardTitle className="text-2xl font-heading">Connexion</CardTitle>
                        <CardDescription>
                            Entrez vos identifiants pour accéder à votre espace
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="directeur@allforyou.fr"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading || seeding}
                                    data-testid="email-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Mot de passe</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={loading || seeding}
                                    data-testid="password-input"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full" 
                                disabled={loading || seeding}
                                data-testid="login-button"
                            >
                                {loading ? 'Connexion...' : seeding ? 'Initialisation...' : 'Se connecter'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
